<template>
  <div
    class="flex gap-x-2 sm:gap-x-4"
    :class="[getMessageClass(message.role)]"
  >
    <!-- Role icon/avatar for AI or System -->
    <div
      v-if="message.role !== AIChatSessionMessageRoleEnum.USER"
      class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100"
    >
      <span
        v-if="message.role === AIChatSessionMessageRoleEnum.SYSTEM"
        class="text-xs font-medium text-purple-600"
      >
        S
      </span>
      <span
        v-else
        class="text-xs font-medium text-purple-600"
      >
        AI
      </span>
    </div>

    <div
      class="grow space-y-1"
      :class="{ 'text-end': message.role === AIChatSessionMessageRoleEnum.USER }"
    >
      <div
        :class="[
          getBubbleClass(message.role),
          { 'border-2 border-yellow-400 bg-yellow-50': isHighlighted }
        ]"
        class="relative"
      >
        <!-- Highlight badge for feedback message -->
        <div
          v-if="isHighlighted"
          class="absolute -top-3 right-2 rounded-full border border-yellow-300 bg-yellow-100 px-2 py-0.5 text-xs
            font-medium text-yellow-800 shadow-sm"
        >
          User reviewed this message
        </div>

        <!-- System message header with collapsible toggle -->
        <div
          v-if="message.role === AIChatSessionMessageRoleEnum.SYSTEM"
          class="flex cursor-pointer items-center justify-between"
          @click="toggleSystemMessageCollapse"
        >
          <div class="text-xs font-medium text-gray-600">System Message</div>
          <button class="text-xs text-gray-500 transition-colors hover:text-gray-700">
            <span v-if="isSystemMessageCollapsed">▼ Show</span>
            <span v-else>▲ Hide</span>
          </button>
        </div>
        <div
          v-else-if="message.role === AIChatSessionMessageRoleEnum.USER"
          class="mb-0.5 text-xs text-gray-500"
        >
          User
        </div>
        <div
          v-else
          class="mb-0.5 text-xs text-gray-500"
        >
          Assistant
        </div>

        <!-- Content with conditional display for system messages -->
        <template v-if="message.role === AIChatSessionMessageRoleEnum.SYSTEM">
          <div
            v-if="!isSystemMessageCollapsed"
            class="markdown-content mt-1 whitespace-pre-wrap text-sm text-gray-800"
            v-html="renderedContent"
          ></div>
        </template>
        <template v-else>
          <div
            class="markdown-content whitespace-pre-wrap text-sm text-gray-800"
            v-html="renderedContent"
          ></div>
        </template>
      </div>

      <!-- Message timestamp -->
      <div
        v-if="message.created_at"
        class="text-xs text-gray-400"
      >
        {{ new Date(message.created_at).toLocaleString() }}
      </div>
    </div>

    <!-- User avatar placeholder -->
    <div
      v-if="message.role === AIChatSessionMessageRoleEnum.USER"
      class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gray-200"
    >
      <span class="text-xs font-medium text-gray-600">U</span>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import { AIChatSessionMessageRoleEnum } from '@/types/generated/types.gen'
  import MarkdownIt from 'markdown-it'

  const md = new MarkdownIt({
    html: true,
    breaks: true,
    linkify: true
  })

  const props = defineProps({
    message: {
      type: Object,
      required: true
    },
    isHighlighted: {
      type: Boolean,
      default: false
    }
  })

  const proseContent = ref(null)
  const isSystemMessageCollapsed = ref(true) // System messages are collapsed by default

  // Toggle system message collapse state
  const toggleSystemMessageCollapse = () => {
    isSystemMessageCollapsed.value = !isSystemMessageCollapsed.value
  }

  // Checks if the message has chunks
  const hasChunks = computed(() => {
    return (
      props.message.role === AIChatSessionMessageRoleEnum.ASSISTANT &&
      props.message.content?.chunks &&
      Array.isArray(props.message.content.chunks)
    )
  })

  // Function to determine message alignment class
  const getMessageClass = (messageRole) => {
    return {
      'justify-end ms-auto': messageRole === AIChatSessionMessageRoleEnum.USER,
      'justify-start': messageRole !== AIChatSessionMessageRoleEnum.USER
    }
  }

  // Function to determine message bubble class
  const getBubbleClass = (messageRole) => {
    if (messageRole === AIChatSessionMessageRoleEnum.USER) {
      return 'inline-block rounded-lg border border-purple-200 bg-purple-100 p-2.5 text-left shadow-sm'
    } else if (messageRole === AIChatSessionMessageRoleEnum.SYSTEM) {
      return 'inline-block rounded-lg border border-amber-100 bg-amber-50 p-2.5 text-left shadow-sm w-full'
    } else {
      return 'inline-block rounded-lg border border-gray-200 bg-white p-2.5 text-left shadow-sm'
    }
  }

  // Simplified entity HTML generator for display purposes
  const getEntityHtml = (entity) => {
    const name = entity.name || 'Entity'
    return `<span class="inline-flex items-center rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800"><strong>${name}</strong></span>`
  }

  // Simplified document HTML generator for display purposes
  const getDocumentHtml = (doc) => {
    const title = doc.title || 'Document'
    const pagesText =
      doc.pages && doc.pages.length > 0
        ? doc.pages.length === 1
          ? `Page ${doc.pages[0]}`
          : `Pages: ${doc.pages.join(', ')}`
        : ''

    return `<span class="inline-flex items-center rounded border border-purple-100 bg-purple-50/50 px-1.5 py-0.5 text-[11px] font-medium text-gray-800">
    <strong>${title}</strong>${pagesText ? ` <span class="text-gray-500">(${pagesText})</span>` : ''}
  </span>`
  }

  // Render content based on message chunks
  const renderedContent = computed(() => {
    try {
      // If the message has no content at all
      if (!props.message.content) return '(No content)'

      // For messages with chunks
      if (hasChunks.value) {
        const content = props.message.content.chunks.reduce((acc, chunk, index) => {
          if (chunk.type === 'text') {
            // Fix spacing issues by ensuring consistent line breaks
            let text = chunk.text

            // If this is not the first text chunk and doesn't start with a newline,
            // and the previous chunk was text, insert a proper space
            if (
              index > 0 &&
              !text.startsWith('\n') &&
              props.message.content.chunks[index - 1].type === 'text' &&
              !props.message.content.chunks[index - 1].text.endsWith('\n')
            ) {
              text = ' ' + text
            }

            return acc + text
          } else if (chunk.type === 'entity') {
            return acc + getEntityHtml(chunk)
          } else if (chunk.type === 'document') {
            return acc + getDocumentHtml(chunk)
          }
          return acc
        }, '')

        // Process the rendered HTML to fix excessive paragraph spacing
        const rawHtml = md.render(content)
        return rawHtml.replace(/<p>\s*<\/p>/g, '')
      }

      // For messages with regular content (string)
      const stringContent = String(props.message.content)
      return md.render(stringContent)
    } catch (error) {
      console.error('Error rendering content:', error)
      return String(props.message.content || '(Error rendering content)')
    }
  })
</script>

<style scoped>
  /* Markdown content styling with controlled spacing */
  .markdown-content {
    font-size: 0.875rem;
    line-height: 1.4;
  }

  /* Typography elements */
  :deep(.markdown-content p) {
    margin: 0.25rem 0;
  }

  :deep(.markdown-content p:first-child) {
    margin-top: 0;
  }

  :deep(.markdown-content p:last-child) {
    margin-bottom: 0;
  }

  :deep(.markdown-content strong) {
    font-weight: 600;
  }

  :deep(.markdown-content em) {
    font-style: italic;
  }

  /* Lists */
  :deep(.markdown-content ul),
  :deep(.markdown-content ol) {
    margin: 0.25rem 0;
    padding-left: 1.25rem;
  }

  :deep(.markdown-content li) {
    margin: 0.125rem 0;
  }

  :deep(.markdown-content li > p) {
    margin: 0.125rem 0;
  }

  /* Headings */
  :deep(.markdown-content h1),
  :deep(.markdown-content h2),
  :deep(.markdown-content h3),
  :deep(.markdown-content h4),
  :deep(.markdown-content h5),
  :deep(.markdown-content h6) {
    margin: 0.5rem 0 0.25rem;
    font-weight: 600;
    line-height: 1.2;
  }

  :deep(.markdown-content h1) {
    font-size: 1.25rem;
  }

  :deep(.markdown-content h2) {
    font-size: 1.125rem;
  }

  :deep(.markdown-content h3) {
    font-size: 1rem;
  }

  /* Code blocks */
  :deep(.markdown-content pre) {
    margin: 0.5rem 0;
    padding: 0.5rem;
    background-color: #f5f5f5;
    border-radius: 0.25rem;
    overflow-x: auto;
  }

  :deep(.markdown-content code) {
    font-family:
      ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
      monospace;
    font-size: 0.875rem;
    padding: 0.125rem 0.25rem;
    background-color: #f5f5f5;
    border-radius: 0.25rem;
  }

  :deep(.markdown-content pre code) {
    padding: 0;
    background-color: transparent;
    border-radius: 0;
  }

  /* Block quotes */
  :deep(.markdown-content blockquote) {
    margin: 0.5rem 0;
    padding-left: 0.75rem;
    border-left: 3px solid #e5e7eb;
    color: #6b7280;
  }

  /* Fix excessive spacing between elements in chunked content */
  :deep(.markdown-content br) {
    display: none;
  }

  :deep(.markdown-content * + *) {
    margin-top: 0.25rem;
  }

  :deep(.markdown-content h1 + p),
  :deep(.markdown-content h2 + p),
  :deep(.markdown-content h3 + p),
  :deep(.markdown-content h4 + p),
  :deep(.markdown-content h5 + p),
  :deep(.markdown-content h6 + p) {
    margin-top: 0.125rem;
  }
</style>
